import React, { useState } from 'react';
import { toast } from 'sonner';
import { Loader2, Plus } from 'lucide-react';
import { api, formatEUR, mensajeError } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { isGestion, puedeMarcarProhibido } from '../../lib/roles';
import { useMarcasPaciente } from '../../lib/marcasPaciente';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { FORMA_ETIQUETA_GRANDE } from '../citas/EtiquetasIntervenciones';
import { AJUSTE_ETIQUETA_MARCA, EtiquetaMarcaPaciente } from '../citas/EtiquetasMarcasPaciente';

/**
 * MARCAS DE PACIENTE EN LA FICHA (administración, 14/10/2026)
 * -----------------------------------------------------------
 * Junto al botón «Paciente prohibido»: una por cada marca ACTIVA de
 * Configuración → «Marcas de paciente». Sin ninguna configurada, nada.
 *
 *  · Con la marca puesta, su indicador junto al nombre, para todos: desde el
 *    21/10/2026 una etiqueta como las de las intervenciones, en el color de
 *    la marca (nunca el rojo de PROHIBIDO).
 *  · Quien puede marcar (los mismos que «Paciente prohibido»: gestión y
 *    doctores) pulsa «+ <marca>» para ponerla y el propio indicador para
 *    quitarla: el mismo botón, con una confirmación que dice qué cambia.
 *
 * El recargo (dinero de los presupuestos) solo se le dice a gestión.
 */

/**
 * El indicador de la marca junto al nombre (administración, 21/10/2026): una
 * ETIQUETA con la misma forma que las rojas «Cirugía — fecha» de la cabecera
 * (EtiquetasIntervenciones.jsx), en el color de la marca y con su nombre. La
 * misma que llevan sus citas (EtiquetasMarcasPaciente.jsx).
 */
export function IndicadorMarcaPaciente({ marca, testId, como = 'span', ...resto }) {
  if (!marca) return null;
  // Desactivada en Configuración (revisión del 14/10/2026): gris y dicho, para
  // que se vea que ya no tiene efectos y se pueda quitar.
  if (marca.activa === false) {
    const Elemento = como;
    return (
      <Elemento
        className={`${AJUSTE_ETIQUETA_MARCA} ${FORMA_ETIQUETA_GRANDE} shrink-0 bg-muted text-muted-foreground border border-border`}
        title={`Marca de paciente desactivada: ${marca.nombre} (ya no tiene efectos)`}
        data-testid={testId}
        data-desactivada="1"
        {...resto}
      >
        {marca.nombre} · marca desactivada
      </Elemento>
    );
  }
  return (
    <EtiquetaMarcaPaciente
      texto={marca.nombre}
      color={marca.color}
      grande
      como={como}
      className="shrink-0"
      testId={testId}
      {...resto}
    />
  );
}

export default function BotonesMarcasPaciente({ paciente, onCambio }) {
  const { user } = useAuth();
  const puede = puedeMarcarProhibido(user);
  const gestion = isGestion(user);
  const [recarga, setRecarga] = useState(0);
  // Todas, también las desactivadas: la que tenga el paciente se enseña como
  // «marca desactivada» y se puede quitar (revisión del 14/10/2026). Las
  // desactivadas que no tiene no ofrecen botón.
  const todas = useMarcasPaciente({ activo: !!paciente?.id, todas: true, recarga });
  const [pendiente, setPendiente] = useState(null); // {marca, poner}
  const [guardando, setGuardando] = useState(false);

  const tiene = new Set(Array.isArray(paciente?.marcas_paciente) ? paciente.marcas_paciente : []);
  const marcas = todas.filter((m) => m.activa !== false || tiene.has(m.id));
  if (!paciente?.id || !marcas.length) return null;

  const confirmar = async () => {
    if (!pendiente) return;
    const { marca, poner } = pendiente;
    setGuardando(true);
    try {
      const ruta = `/api/patients/${paciente.id}/marcas-paciente/${marca.id}`;
      const { data } = poner ? await api.post(ruta) : await api.delete(ruta);
      const n = Number(data?.citas_actualizadas || 0);
      toast.success(poner
        ? `Marcado como «${marca.nombre}»${n ? ` · ${n} cita${n === 1 ? '' : 's'} futura${n === 1 ? '' : 's'} con la marca` : ''}`
        : `Quitada la marca «${marca.nombre}»`);
      setPendiente(null);
      setRecarga((x) => x + 1);
      onCambio?.(data);
    } catch (e) {
      toast.error(mensajeError(e, 'No se ha podido cambiar la marca'));
    } finally {
      setGuardando(false);
    }
  };

  return (
    <span className="inline-flex flex-wrap items-center gap-2 align-middle font-sans text-base font-normal" data-testid="marcas-paciente">
      {marcas.map((m) => {
        const puesta = tiene.has(m.id);
        if (puesta) {
          return puede ? (
            <IndicadorMarcaPaciente
              key={m.id}
              marca={m}
              como="button"
              type="button"
              onClick={() => setPendiente({ marca: m, poner: false })}
              aria-pressed="true"
              testId={`marca-paciente-${m.id}`}
            />
          ) : (
            <IndicadorMarcaPaciente key={m.id} marca={m} testId={`marca-paciente-${m.id}`} />
          );
        }
        return puede ? (
          <Button
            key={m.id}
            type="button"
            size="sm"
            variant="outline"
            onClick={() => setPendiente({ marca: m, poner: true })}
            aria-pressed="false"
            className="h-7 px-2 text-xs text-muted-foreground"
            title={`Marcar como «${m.nombre}»`}
            data-testid={`boton-marca-paciente-${m.id}`}
          >
            <Plus className="w-3.5 h-3.5 mr-1" /> {m.nombre}
          </Button>
        ) : null;
      })}

      <Dialog open={!!pendiente} onOpenChange={(abierto) => { if (!abierto) setPendiente(null); }}>
        <DialogContent className="max-w-md" data-testid="marca-paciente-dialogo">
          {pendiente && (
            <>
              <DialogHeader>
                <DialogTitle>
                  {pendiente.poner ? `Marcar como «${pendiente.marca.nombre}»` : `Quitar «${pendiente.marca.nombre}»`}
                </DialogTitle>
              </DialogHeader>
              <ul className="list-disc ml-5 space-y-1 text-sm text-[#13231E]" data-testid="marca-paciente-efectos">
                {pendiente.poner ? (
                  <>
                    <li>Sus citas mostrarán la etiqueta «{pendiente.marca.texto_en_citas}» junto a su nombre, también las que ya tiene a partir de hoy.</li>
                    {gestion && Number(pendiente.marca.recargo_cirugia) > 0 && (
                      <li>
                        Sus presupuestos de cirugía nuevos incluirán un recargo de {formatEUR(pendiente.marca.recargo_cirugia)} dentro
                        del precio de la cirugía (el paciente no lo ve aparte).
                      </li>
                    )}
                  </>
                ) : (
                  <>
                    {pendiente.marca.activa === false && (
                      <li>Esta marca está desactivada en Configuración: ya no tiene efectos.</li>
                    )}
                    <li>Sus citas futuras dejarán de mostrar la etiqueta «{pendiente.marca.texto_en_citas}». Las pasadas se quedan como estaban.</li>
                    {gestion && Number(pendiente.marca.recargo_cirugia) > 0 && (
                      <li>Los presupuestos nuevos dejarán de llevar el recargo; los ya hechos no cambian.</li>
                    )}
                  </>
                )}
              </ul>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setPendiente(null)}>Cancelar</Button>
                <Button
                  type="button"
                  onClick={confirmar}
                  disabled={guardando}
                  className="bg-[#2E6E5E] hover:bg-[#1E4A3F] text-white"
                  data-testid="marca-paciente-confirmar"
                >
                  {guardando && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                  {pendiente.poner ? 'Marcar' : 'Quitar la marca'}
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </span>
  );
}
